import { useMemo } from 'react';

import { MessagePairType } from 'components/ChatAI/VehicleMatchmaker/hooks/useMessagePairing';

import type { RepairCostEstimate } from '@ltvco/refresh-lib/api';
import type { Message } from '@ltvco/refresh-lib/v2';

interface EstimateData extends RepairCostEstimate {
  error?: string;
  [key: string]: unknown;
}

type MessageWithEstimate = Message & {
  repairCostEstimateData?: EstimateData;
};

interface UseMessagePairingReturn {
  pairs: MessagePairType[];
  firstEstimateAssistantIndex: number;
  firstEstimateData: RepairCostEstimate | null | undefined;
  hasError: boolean;
}

/**
 * Custom hook to handle message pairing logic
 * Pairs user messages with assistant responses and tracks estimate data
 */
export const useMessagePairing = (
  messages: Message[],
): UseMessagePairingReturn => {
  return useMemo(() => {
    const messagesWithEstimate = messages as MessageWithEstimate[];

    // Find first successful estimate
    const firstEstimateAssistantIndex = messagesWithEstimate.findIndex(
      (message) =>
        message.role === 'assistant' &&
        message.repairCostEstimateData &&
        !message.repairCostEstimateData.error,
    );

    const firstEstimateData =
      firstEstimateAssistantIndex !== -1
        ? messagesWithEstimate[firstEstimateAssistantIndex]
            .repairCostEstimateData
        : null;
    // Check for errors
    const hasError = messagesWithEstimate.some(
      (msg) =>
        msg.role === 'assistant' &&
        ((msg.repairCostEstimateData && msg.repairCostEstimateData.error) ||
          (msg.content && msg.repairCostEstimateData === null)),
    );

    // Create pairs
    const pairs: MessagePairType[] = [];
    for (let i = 0; i < messagesWithEstimate.length; i++) {
      const userMsg = messagesWithEstimate[i]!;
      if (userMsg.role !== 'user') continue;

      const maybeAssistant = messagesWithEstimate[i + 1];
      const assistantMsg =
        maybeAssistant && maybeAssistant.role === 'assistant'
          ? maybeAssistant
          : null;
      const assistantIndex = assistantMsg ? i + 1 : null;

      // Skip rendering the pair whose assistant message is rendered
      // as the RepairCostEstimatorResultCard to avoid extra vertical spacing.
      if (
        assistantIndex !== null &&
        assistantIndex === firstEstimateAssistantIndex
      ) {
        continue;
      }

      pairs.push({
        id: `pair-${i}`,
        user: userMsg,
        assistant: assistantMsg,
        userIndex: i,
        assistantIndex,
      });
    }

    return {
      pairs,
      firstEstimateAssistantIndex,
      firstEstimateData,
      hasError,
    };
  }, [messages]);
};
